// Davriy so'rov (Q-052): server yukini kamaytirish.
// - Ekran yashirin (telefon qulflangan, boshqa oyna) — so'rov to'xtaydi yoki siyraklashadi; ko'ringanda darhol yangilanadi.
// - Xato/internet yo'q — oraliq ikki barobardan uzayadi (eng ko'pi 60 s); internet qaytsa — darhol.
import { useEffect, useRef } from 'react';

export const POLL_MAX_MS = 60_000;

export interface PollPlan {
  /** Ekran ko'rinib turganda */
  intervalMs: number;
  /** Ekran yashirin bo'lganda: null — umuman so'ramaydi */
  hiddenMs: number | null;
  /** Birinchi so'rov darhol emas, bir oraliqdan keyin (ma'lumot hozirgina olingan bo'lsa) */
  delayFirst?: boolean;
}

/** Keyingi so'rovgacha kutish (ms); null — ekran ko'ringuncha kutadi */
export function pollDelay(plan: PollPlan, hidden: boolean, failures: number): number | null {
  const base = hidden ? plan.hiddenMs : plan.intervalMs;
  if (base === null) return null;
  if (failures <= 0) return base;
  return Math.min(Math.max(base, POLL_MAX_MS / 8) * 2 ** Math.min(failures - 1, 6), Math.max(base, POLL_MAX_MS));
}

/** Agregator buyurtmasi (Q-049): smena ochiq kassa: tez; smenasiz yoki ekran yashirin — siyrak (Q-052) */
export function incomingPlan(shiftOpen: boolean): PollPlan {
  return shiftOpen ? { intervalMs: 8_000, hiddenMs: 30_000 } : { intervalMs: 60_000, hiddenMs: 60_000 };
}

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * `run` — bitta so'rov; true — muvaffaqiyatli, false — xato (oraliq uzayadi).
 * Ekran yana ko'ringanda yoki internet qaytganda — oxirgi so'rovdan `intervalMs` o'tgan bo'lsa, darhol so'raydi.
 */
export function usePolling(run: () => Promise<boolean>, enabled: boolean, plan: PollPlan): void {
  const runRef = useRef(run);
  useEffect(() => {
    runRef.current = run;
  }, [run]);
  const { intervalMs, hiddenMs, delayFirst = false } = plan;

  useEffect(() => {
    if (!enabled) return;
    const p: PollPlan = { intervalMs, hiddenMs };
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let busy = false;
    let failures = 0;
    let last = 0;

    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (stop) return;
      const d = pollDelay(p, isHidden(), failures);
      if (d !== null) timer = setTimeout(() => void tick(), d);
    };
    const tick = async () => {
      if (stop || busy) return;
      busy = true;
      let ok = false;
      try {
        ok = await runRef.current();
      } catch {
        ok = false;
      }
      busy = false;
      last = Date.now();
      failures = ok ? 0 : failures + 1;
      schedule();
    };
    const wake = () => {
      if (stop || busy || isHidden()) {
        schedule();
        return;
      }
      if (Date.now() - last >= p.intervalMs) {
        failures = 0;
        void tick();
      } else schedule();
    };
    const onOnline = () => {
      failures = 0;
      wake();
    };

    if (delayFirst) {
      last = Date.now();
      schedule();
    } else void tick();
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', onOnline);
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, intervalMs, hiddenMs, delayFirst]);
}
